import { useState, useCallback, useEffect, useRef } from "react";
import { Dialog, Button, Text, Input, AlertBanner } from "snap-design-system";
import { KeyRound, Eye, EyeOff, Info, RotateCcw, LogOut } from "lucide-react";
import PasswordStrengthMeter, { validatePasswordNIST } from "~/components/PasswordStrengthMeter";
import { useReencryption, checkResumableBatch } from "~/hooks/useReencryption";
import { useReencryptionWs } from "~/hooks/useReencryptionWs";
import { useAdminOpkUnlock } from "~/hooks/useAdminOpkUnlock";
import { ReencryptionProgress } from "~/containers/account/configuration/security/ReencryptionProgress";
import { CRYPTO_SERVICE } from "~/workers/crypto";
import { verifierPhrase } from "~/helpers/constants";
import { encryptionWorker } from "~/services/workers/encryptionWorker.service";
import { StepIndicator } from "~/components/StepIndicator";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import type { PendingBatchResponse } from "~/domain/OrgEncryption";
import { toast } from "sonner";
import { eventBus } from "~/helpers/eventBus.helper";
import { useUserRewrapWs } from "~/hooks/useUserRewrapWs";

interface ResetUserKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetUserId: string;
  targetUserName: string;
  adminWrap: {
    encrypted_opk: string;
    wrap_iv: string;
    wrap_salt: string;
  } | null;
  onSelfResetComplete?: () => void;
  /** Called when reencryption fully completes — use this to dismiss the pending reset request */
  onResetComplete?: () => void;
}

const TOTAL_STEPS = 3;

export function ResetUserKeyDialog({
  open,
  onOpenChange,
  targetUserId,
  targetUserName,
  adminWrap,
  onSelfResetComplete,
  onResetComplete,
}: ResetUserKeyDialogProps) {
  const [step, setStep] = useState(1);
  const [adminPassword, setAdminPassword] = useState("");
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [confirmTempPassword, setConfirmTempPassword] = useState("");
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showConfirmTempPassword, setShowConfirmTempPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opkBytes, setOpkBytes] = useState<ArrayBuffer | null>(null);
  const [pendingBatch, setPendingBatch] = useState<PendingBatchResponse | null>(null);
  const [isResumingMode, setIsResumingMode] = useState(false);
  const [isReindexingHmacs, setIsReindexingHmacs] = useState(false);

  // Store the new user key for HMAC re-indexing after self-reset
  const newUserKeyRef = useRef<ArrayBuffer | null>(null);
  const hmacReindexStartedRef = useRef(false);

  const {
    startReencryptionFlow,
    resumeReencryptionFlow,
    handleItemsMessage,
    progress,
    activeBatchId,
  } = useReencryption();
  const { unlockOpk } = useAdminOpkUnlock();

  // User re-wrap WS for HMAC re-indexing on self-reset
  const {
    start: startHmacRewrap,
    progress: hmacRewrapProgress,
    isComplete: hmacRewrapComplete,
  } = useUserRewrapWs();

  // Single WS connection — opens when activeBatchId is set, pushes items and progress
  const { isComplete, requestNextPage, requestProgress, onItems } =
    useReencryptionWs(activeBatchId);

  // Wire the WS items callback to the reencryption processor
  useEffect(() => {
    onItems((msg) => {
      handleItemsMessage(msg, requestNextPage, requestProgress);
    });
  }, [onItems, handleItemsMessage, requestNextPage, requestProgress]);

  const isSelfReset = !!onSelfResetComplete;

  // When WS reports complete on a self-reset:
  // Start HMAC re-indexing before logging out (HMACs are derived from user key,
  // so they must be re-computed with the new key before the session ends)
  useEffect(() => {
    if (!isComplete || step !== 3 || !isSelfReset || !onSelfResetComplete) return;
    if (hmacReindexStartedRef.current) return;
    hmacReindexStartedRef.current = true;

    // Grace period — the backend skips forced logout for self-reset,
    // but we still need to suppress 401 auto-logout during HMAC re-indexing
    eventBus.startLogoutGracePeriod(120_000);

    const newKey = newUserKeyRef.current;
    if (!newKey) {
      // No key available — skip HMAC re-indexing, go straight to logout
      toast.loading("Re-criptografia concluída. Você será desconectado agora...", {
        description: "Faça login novamente com sua nova senha de criptografia pessoal.",
        duration: 3000,
      });
      const timer = setTimeout(() => {
        eventBus.clearLogoutGracePeriod();
        onSelfResetComplete();
      }, 3000);
      return () => clearTimeout(timer);
    }

    // Start HMAC re-indexing: DEKs are already wrapped with the new key,
    // so oldKey = newKey (re-wrap is a no-op, but HMACs get recomputed)
    setIsReindexingHmacs(true);
    toast.loading("Atualizando índices de busca...", {
      description: "Aguarde antes de ser desconectado.",
      duration: Infinity,
      id: "hmac-reindex-toast",
    });
    startHmacRewrap(targetUserId, newKey, newKey);
  }, [isComplete, step, isSelfReset]);

  // When HMAC re-indexing completes on self-reset → dismiss pending request + logout
  useEffect(() => {
    if (!hmacRewrapComplete || !isSelfReset || !onSelfResetComplete) return;

    // Dismiss pending reset request if any (fire-and-forget, no cache invalidation needed)
    onResetComplete?.();

    toast.success("Índices de busca atualizados. Você será desconectado agora...", {
      id: "hmac-reindex-toast",
      description: "Faça login novamente com sua nova senha de criptografia pessoal.",
      duration: 3000,
    });

    const timer = setTimeout(() => {
      eventBus.clearLogoutGracePeriod();
      onSelfResetComplete();
    }, 3000);
    return () => clearTimeout(timer);
  }, [hmacRewrapComplete, isSelfReset]);

  // When WS reports complete for another user's reset — close the dialog automatically
  useEffect(() => {
    if (!isComplete || step !== 3 || isSelfReset) return;
    onResetComplete?.();
    const timer = setTimeout(() => {
      onOpenChange(false);
      resetState();
    }, 1500);
    return () => clearTimeout(timer);
  }, [isComplete, step, isSelfReset]);

  // When dialog opens, check for a resumable batch
  useEffect(() => {
    if (!open) return;
    checkResumableBatch(targetUserId).then((batch) => setPendingBatch(batch));
  }, [open, targetUserId]);

  const resetState = useCallback(() => {
    setStep(1);
    setAdminPassword("");
    setShowAdminPassword(false);
    setTempPassword("");
    setConfirmTempPassword("");
    setShowTempPassword(false);
    setShowConfirmTempPassword(false);
    setIsProcessing(false);
    setError(null);
    setOpkBytes(null);
    setPendingBatch(null);
    setIsResumingMode(false);
    setIsReindexingHmacs(false);
    newUserKeyRef.current = null;
    hmacReindexStartedRef.current = false;
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    // Block close during HMAC re-indexing on self-reset
    if (!nextOpen && isReindexingHmacs && isSelfReset) return;
    // Allow closing during re-encryption for other users — toast persists outside dialog
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  };

  // Step 1: Admin enters password → unlock OPK, then always go to step 2
  const handleUnlockOpk = async () => {
    if (!adminWrap) {
      setError("Dados de criptografia do administrador não encontrados.");
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const decryptedOpk = await unlockOpk(adminPassword, adminWrap);
      setOpkBytes(decryptedOpk);
      setStep(2);
    } catch {
      setError("Senha de administrador incorreta ou dados corrompidos.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 2 → 3: Set temp password and start re-encryption (or resume)
  const handleStartReencryption = async () => {
    if (!opkBytes) return;
    setIsProcessing(true);
    setError(null);
    setStep(3);

    // For self-reset: start grace period early — the backend will skip forced logout
    // for self-reset, but we still need to suppress 401 auto-logout until HMAC
    // re-indexing finishes. 120s covers even large batches; cleared on intentional logout.
    if (isSelfReset) {
      eventBus.startLogoutGracePeriod(120_000);
    }

    try {
      const saltBytes = crypto.getRandomValues(new Uint8Array(16));
      const newSalt = Array.from(saltBytes).map(b => b.toString(16).padStart(2, "0")).join("");

      const passwordBytes = new TextEncoder().encode(tempPassword);
      const saltEncoded = new TextEncoder().encode(newSalt);
      const { key: newUserDek } = await CRYPTO_SERVICE.deriveKey(passwordBytes, saltEncoded);
      const rawKey = newUserDek.buffer.slice(newUserDek.byteOffset, newUserDek.byteOffset + newUserDek.byteLength) as ArrayBuffer;

      // Store the new key for HMAC re-indexing on self-reset
      if (isSelfReset) {
        newUserKeyRef.current = rawKey;
      }

      if (isResumingMode && pendingBatch) {
        // Resume — batch already exists, WS will connect and push items
        await resumeReencryptionFlow(pendingBatch, opkBytes, rawKey);
      } else {
        // New flow — create batch, WS will connect when activeBatchId is set
        const encryptedVerifier = await encryptionWorker.encrypt(verifierPhrase, rawKey);
        const newVerifier = {
          encrypted: encryptedVerifier.encrypted,
          iv: encryptedVerifier.iv,
          salt: newSalt,
        };
        await startReencryptionFlow(targetUserId, newSalt, newVerifier, opkBytes, rawKey);
      }
    } catch (err) {
      if (isSelfReset) eventBus.clearLogoutGracePeriod();
      await createErrorHandler(
        "Erro ao iniciar re-criptografia.",
        "Erro na re-criptografia",
      )(err);
      setStep(2);
    } finally {
      setIsProcessing(false);
    }
  };

  const tempPasswordValidation = validatePasswordNIST(tempPassword);
  const tempPasswordsMatch = tempPassword === confirmTempPassword;
  const canProceedFromTempPassword =
    tempPasswordValidation.isValid && tempPasswordsMatch && confirmTempPassword.length > 0;
  const canProceedStep2 = isResumingMode ? tempPassword.length > 0 : canProceedFromTempPassword;

  const renderStep1 = () => (
    <div className="space-y-4">
      {pendingBatch && !isResumingMode && (
        <AlertBanner variant="warning" icon={<RotateCcw className="size-5" />}>
          <div className="flex flex-col gap-1">
            <Text variant="body-sm" color="warning">
              Existe uma re-criptografia em andamento para este usuário
              ({pendingBatch.completed_count}/{pendingBatch.total_count} concluídos).
            </Text>
            <button
              type="button"
              className="text-left text-sm text-warning-700 underline cursor-pointer"
              onClick={() => setIsResumingMode(true)}
            >
              Retomar processo existente
            </button>
          </div>
        </AlertBanner>
      )}
      {isResumingMode && pendingBatch && (
        <AlertBanner
          variant="information"
          icon={<Info className="size-5" />}
          message={<>Retomando re-criptografia existente ({pendingBatch.completed_count}/{pendingBatch.total_count} já concluídos).</>}
        />
      )}
      <Text variant="body-sm" color="subtle">
        {isResumingMode
          ? "Desbloqueie a chave da organização para retomar a re-criptografia."
          : <>Para redefinir a senha de criptografia pessoal de <strong>{targetUserName}</strong>, desbloqueie a chave da organização.</>
        }
      </Text>
      <div className="space-y-2">
        <Text variant="body-sm">Sua senha de administrador</Text>
        <div className="relative">
          <Input
            id="reset-admin-password"
            type={showAdminPassword ? "text" : "password"}
            value={adminPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminPassword(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" && adminPassword.trim() && !isProcessing) handleUnlockOpk();
            }}
            placeholder="Digite sua senha de administrador"
            fullWidth
            autoFocus
            disabled={isProcessing}
            data-testid="input-reset-admin-password"
          />
          <button
            type="button"
            onClick={() => setShowAdminPassword(p => !p)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-80 hover:opacity-100 cursor-pointer focus:outline-none"
            aria-label={showAdminPassword ? "Esconder senha" : "Mostrar senha"}
          >
            {showAdminPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <AlertBanner
        variant="information"
        icon={<Info className="size-5" />}
        message={
          isSelfReset
            ? <>Defina sua nova senha de criptografia pessoal. Esta será a senha que você usará para acessar seus relatórios criptografados.</>
            : isResumingMode
              ? <>Digite a senha temporária definida anteriormente para <strong>{targetUserName}</strong>.</>
              : <>Defina uma senha de criptografia pessoal temporária para <strong>{targetUserName}</strong>. O usuário deverá criar uma nova senha no próximo acesso.</>
        }
      />
      <div className="space-y-2">
        <Text variant="body-sm">
          {isSelfReset
            ? "Nova senha de criptografia pessoal"
            : isResumingMode
              ? "Senha temporária definida anteriormente"
              : "Senha de criptografia pessoal temporária"}
        </Text>
        <div className="relative">
          <Input
            id="reset-temp-password"
            type={showTempPassword ? "text" : "password"}
            value={tempPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempPassword(e.target.value)}
            placeholder={
              isSelfReset
                ? "Digite sua nova senha"
                : isResumingMode
                  ? "Digite a senha temporária anterior"
                  : "Digite a senha temporária"
            }
            fullWidth
            autoFocus
            data-testid="input-reset-temp-password"
          />
          <button
            type="button"
            onClick={() => setShowTempPassword(p => !p)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-80 hover:opacity-100 cursor-pointer focus:outline-none"
            aria-label={showTempPassword ? "Esconder senha" : "Mostrar senha"}
          >
            {showTempPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {!isResumingMode && <PasswordStrengthMeter password={tempPassword} />}
      </div>
      {!isResumingMode && (
        <div className="space-y-2">
          <Text variant="body-sm">
            {isSelfReset ? "Confirmar nova senha" : "Confirmar senha de criptografia pessoal temporária"}
          </Text>
          <div className="relative">
            <Input
              id="reset-confirm-temp-password"
              type={showConfirmTempPassword ? "text" : "password"}
              value={confirmTempPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmTempPassword(e.target.value)}
              placeholder={isSelfReset ? "Confirme sua nova senha" : "Confirme a senha temporária"}
              fullWidth
              data-testid="input-reset-confirm-temp-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmTempPassword(p => !p)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-80 hover:opacity-100 cursor-pointer focus:outline-none"
              aria-label={showConfirmTempPassword ? "Esconder senha" : "Mostrar senha"}
            >
              {showConfirmTempPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {confirmTempPassword && !tempPasswordsMatch && (
            <Text variant="body-sm" className="text-danger-500">As senhas não coincidem.</Text>
          )}
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4 py-4">
      <ReencryptionProgress progress={progress} />
      {progress.isRunning ? (
        <Text variant="body-sm" color="subtle" className="text-center">
          Você pode fechar esta janela. O progresso continuará em segundo plano.
        </Text>
      ) : isComplete && isSelfReset && !isReindexingHmacs ? (
        <AlertBanner
          variant="information"
          icon={<Info className="size-5" />}
          title="Re-criptografia concluída. Atualizando índices de busca..."
        />
      ) : isReindexingHmacs && !hmacRewrapComplete ? (
        <div className="space-y-3">
          <ReencryptionProgress progress={{
            total: hmacRewrapProgress.total,
            completed: hmacRewrapProgress.processed,
            failed: hmacRewrapProgress.failed,
            isRunning: hmacRewrapProgress.isRunning,
          }} />
          <Text variant="body-sm" color="subtle" className="text-center">
            Atualizando índices de busca...
          </Text>
        </div>
      ) : isComplete && isSelfReset && hmacRewrapComplete ? (
        <div className="space-y-4">
          <AlertBanner
            variant="success"
            icon={<Info className="size-5" />}
            title="Tudo pronto. Você será desconectado em instantes para que possa entrar com sua nova senha."
          />
          <div className="flex flex-col items-center py-2 space-y-2">
            <LogOut className="w-8 h-8 text-accent-500" />
            <Text variant="body-sm" color="subtle" className="text-center">
              Faça login novamente com sua nova senha de criptografia pessoal.
            </Text>
          </div>
        </div>
      ) : (
        <Text variant="body-sm" color="subtle" className="text-center">
          Processamento concluído.
        </Text>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return isResumingMode ? "Retomar Re-criptografia" : "Desbloquear Chave da Organização";
      case 2: return isResumingMode ? "Confirmar Senha Temporária" : isSelfReset ? "Nova Senha de Criptografia" : "Definir Senha Temporária";
      case 3: return "Re-criptografando Relatórios";
      default: return "Redefinir Senha do Usuário";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal>
      <Dialog.Content
        className="max-w-lg"
        aria-describedby={undefined}
        showClose={!(isReindexingHmacs && isSelfReset)}
        onInteractOutside={(e) => {
          if (isReindexingHmacs && isSelfReset) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isReindexingHmacs && isSelfReset) e.preventDefault();
        }}
      >
        <Dialog.Header>
          <Dialog.Icon><KeyRound size={20} /></Dialog.Icon>
          <Dialog.Title>{getStepTitle()}</Dialog.Title>
        </Dialog.Header>

        <Dialog.Body>
          <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />
          {renderCurrentStep()}
          {error && <Text variant="body-sm" className="text-danger-500 mt-3">{error}</Text>}
        </Dialog.Body>

        <Dialog.Footer>
          {step === 1 && (
            <>
              <Dialog.Close asChild><Button variant="ghost">Cancelar</Button></Dialog.Close>
              <Button onClick={handleUnlockOpk} disabled={!adminPassword.trim() || isProcessing}>
                {isProcessing ? "Verificando..." : "Continuar"}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="ghost" onClick={() => setStep(1)}>Voltar</Button>
              <Button disabled={!canProceedStep2 || isProcessing} onClick={handleStartReencryption}>
                {isResumingMode ? "Retomar Re-criptografia" : isSelfReset ? "Redefinir Minha Senha" : "Redefinir Senha"}
              </Button>
            </>
          )}
          {step === 3 && (
            (isComplete && isSelfReset) || isReindexingHmacs ? null : (
              <Dialog.Close asChild>
                <Button variant="ghost">Fechar (progresso continua em segundo plano)</Button>
              </Dialog.Close>
            )
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
